import React, { useState, useEffect, useCallback } from 'react';

/* ─── TOKENS ─────────────────────────────────────────────────────────── */
const T = {
  bg:      '#0a0c10',
  bg2:     '#0e1117',
  bg3:     '#13171f',
  surface: '#1a1f2b',
  border:  'rgba(255,255,255,.07)',
  borderHi:'rgba(255,255,255,.12)',
  white:   '#e8edf5',
  muted:   'rgba(232,237,245,.45)',
  dim:     'rgba(232,237,245,.22)',
  green:   '#3dd68c',
  red:     '#ff5f57',
  yellow:  '#fbbf24',
  blue:    '#4d9fff',
  purple:  '#b78cff',
  orange:  '#ff9f43',
  cyan:    '#00d4ff',
  mono:    '"JetBrains Mono","Fira Code","Cascadia Code",monospace',
  sans:    '"DM Sans","Outfit","system-ui",sans-serif',
};

/* ─── STYLES ─────────────────────────────────────────────────────────── */
const S = {
  editor: {
    width: '100%',
    minHeight: 160,
    padding: '14px 16px',
    background: T.bg,
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    color: T.white,
    fontSize: 12.5,
    fontFamily: T.mono,
    lineHeight: 1.65,
    resize: 'vertical',
    outline: 'none',
    transition: 'border-color .15s',
    boxSizing: 'border-box',
    display: 'block',
  },
  editorReadOnly: {
    width: '100%',
    minHeight: 160,
    padding: '14px 16px',
    background: T.bg3,
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    color: T.white,
    fontSize: 12.5,
    fontFamily: T.mono,
    lineHeight: 1.65,
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    background: T.bg,
    border: `1px solid ${T.border}`,
    borderRadius: 7,
    color: T.white,
    fontSize: 13,
    fontFamily: T.mono,
    outline: 'none',
    transition: 'border-color .15s',
    boxSizing: 'border-box',
    display: 'block',
  },
};

/* ─── ATOMS ──────────────────────────────────────────────────────────── */
function Label({ children }) {
  return (
    <div style={{
      fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em',
      textTransform: 'uppercase', color: T.dim,
      fontFamily: T.sans, marginBottom: 6,
    }}>{children}</div>
  );
}

function Pill({ children, active, onClick, color = T.blue }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 6,
      border: `1px solid ${active ? color + '60' : T.border}`,
      background: active ? color + '18' : 'transparent',
      color: active ? color : T.muted,
      fontSize: 12.5, fontWeight: 600, fontFamily: T.sans,
      cursor: 'pointer', letterSpacing: '.03em',
      transition: 'all .15s', whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const [hover,  setHover]  = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '5px 12px', borderRadius: 5,
        border: `1px solid ${hover ? T.borderHi : T.border}`,
        background: hover ? T.surface : 'transparent',
        color: copied ? T.green : hover ? T.white : T.muted,
        fontSize: 11.5, fontFamily: T.sans,
        cursor: 'pointer', transition: 'all .12s',
        display: 'inline-flex', alignItems: 'center', gap: 5,
      }}>
      {copied ? '✓ Copied' : '⧉ Copy'}
    </button>
  );
}

function ActionBtn({ onClick, children, color = T.blue }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '6px 16px', borderRadius: 6,
        border: `1px solid ${hover ? color + '80' : color + '50'}`,
        background: hover ? color + '25' : color + '15',
        color: color,
        fontSize: 12.5, fontWeight: 600, fontFamily: T.sans,
        cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
      {children}
    </button>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      padding: '8px 12px', borderRadius: 7, marginTop: 8,
      background: T.red + '12', border: `1px solid ${T.red}40`,
      fontSize: 12, color: T.red, fontFamily: T.mono,
    }}>{msg}</div>
  );
}

function TwoCol({ children }) {
  return (
    <div className="dte-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {children}
    </div>
  );
}

function Col({ label, children }) {
  return (
    <div>
      {label && <Label>{label}</Label>}
      {children}
    </div>
  );
}

/* ─── YAML FORMATTER ─────────────────────────────────────────────────── */
export function YAMLFormatter() {
  const [input,  setInput]  = useState('');
  const [output, setOutput] = useState('');
  const [error,  setError]  = useState('');

  const format = () => {
    setError('');
    try {
      setOutput(input.split('\n').filter(l => l.trim()).join('\n'));
    } catch(e) { setError(e.message); }
  };

  const toJson = () => {
    setError('');
    try {
      const obj = {};
      input.split('\n').forEach(line => {
        const m = line.match(/^(\w[\w.]*)\s*:\s*(.+)$/);
        if (m) obj[m[1]] = isNaN(m[2]) ? m[2].replace(/^['"]|['"]$/g, '') : Number(m[2]);
      });
      setOutput(JSON.stringify(obj, null, 2));
    } catch(e) { setError(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        <ActionBtn onClick={format}>Format YAML</ActionBtn>
        <ActionBtn onClick={toJson} color={T.cyan}>→ JSON</ActionBtn>
      </div>
      <TwoCol>
        <Col label="Input YAML">
          <textarea
            style={S.editor}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={"name: John\nage: 30\ncity: Vancouver"}
          />
        </Col>
        <Col label="Output">
          <textarea style={S.editorReadOnly} value={output} readOnly/>
        </Col>
      </TwoCol>
      <ErrorBox msg={error}/>
      {output && <div style={{ marginTop: 8 }}><CopyBtn value={output}/></div>}
    </div>
  );
}

/* ─── BASE64 ─────────────────────────────────────────────────────────── */
export function Base64Tool() {
  const [input,  setInput]  = useState('');
  const [output, setOutput] = useState('');
  const [mode,   setMode]   = useState('encode');
  const [error,  setError]  = useState('');

  useEffect(() => {
    setError(''); setOutput('');
    if (!input.trim()) return;
    try {
      if (mode === 'encode') setOutput(btoa(unescape(encodeURIComponent(input))));
      else setOutput(decodeURIComponent(escape(atob(input))));
    } catch { setError('Invalid Base64 input'); }
  }, [input, mode]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <Pill active={mode === 'encode'} onClick={() => setMode('encode')}>Encode</Pill>
        <Pill active={mode === 'decode'} onClick={() => setMode('decode')}>Decode</Pill>
      </div>
      <TwoCol>
        <Col label={mode === 'encode' ? 'Plain Text' : 'Base64 Input'}>
          <textarea
            style={S.editor}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={mode === 'encode' ? 'Hello, World!' : 'SGVsbG8sIFdvcmxkIQ=='}
          />
        </Col>
        <Col label={mode === 'encode' ? 'Base64 Output' : 'Decoded Text'}>
          <textarea style={S.editorReadOnly} value={output} readOnly/>
        </Col>
      </TwoCol>
      <ErrorBox msg={error}/>
      {output && <div style={{ marginTop: 8 }}><CopyBtn value={output}/></div>}
    </div>
  );
}

/* ─── URL ENCODER/DECODER ────────────────────────────────────────────── */
export function URLTool() {
  const [input, setInput] = useState('');
  const [mode,  setMode]  = useState('encode');

  const result = (() => {
    if (!input) return '';
    try { return mode === 'encode' ? encodeURIComponent(input) : decodeURIComponent(input); }
    catch { return 'Invalid URL-encoded string'; }
  })();

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <Pill active={mode === 'encode'} onClick={() => setMode('encode')}>Encode</Pill>
        <Pill active={mode === 'decode'} onClick={() => setMode('decode')}>Decode</Pill>
      </div>
      <TwoCol>
        <Col label="Input">
          <textarea
            style={S.editor}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={mode === 'encode' ? 'https://example.com/search?q=hello world' : 'https%3A%2F%2Fexample.com'}
          />
        </Col>
        <Col label="Output">
          <textarea style={S.editorReadOnly} value={result} readOnly/>
        </Col>
      </TwoCol>
      {result && <div style={{ marginTop: 8 }}><CopyBtn value={result}/></div>}
    </div>
  );
}

/* ─── JWT DECODER ────────────────────────────────────────────────────── */
export function JWTDecoder() {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');

  const decode = (t) => {
    const parts = t.trim().split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT — must have 3 parts separated by dots');
    const dec = s => JSON.parse(decodeURIComponent(escape(atob(s.replace(/-/g, '+').replace(/_/g, '/')))));
    return { header: dec(parts[0]), payload: dec(parts[1]) };
  };

  let parsed = null;
  if (token.trim()) {
    try { parsed = decode(token); }
    catch(e) { if (!error) setError(e.message); }
  }

  const isExpired = parsed?.payload?.exp
    ? parsed.payload.exp * 1000 < Date.now()
    : null;

  return (
    <div>
      <Label>JWT Token</Label>
      <textarea
        style={{ ...S.editor, minHeight: 80, fontSize: 11 }}
        value={token}
        onChange={e => { setToken(e.target.value); setError(''); }}
        placeholder="Paste your JWT token here…"
      />
      {!parsed && token && <ErrorBox msg={error}/>}
      {parsed && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isExpired !== null && (
            <div style={{
              padding: '6px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600,
              background: isExpired ? T.red + '12' : T.green + '12',
              border: `1px solid ${isExpired ? T.red + '40' : T.green + '40'}`,
              color: isExpired ? T.red : T.green,
            }}>
              {isExpired ? '⚠ Token expired' : '✓ Token valid'}
              {parsed.payload.exp && ` — expires ${new Date(parsed.payload.exp * 1000).toLocaleString()}`}
            </div>
          )}
          {[['Header', parsed.header], ['Payload', parsed.payload]].map(([label, data]) => (
            <div key={label}>
              <Label>{label}</Label>
              <pre style={{
                background: T.bg3, border: `1px solid ${T.border}`,
                borderRadius: 8, padding: '12px 14px',
                fontSize: 12, fontFamily: T.mono, color: T.white,
                overflow: 'auto', margin: 0, lineHeight: 1.65,
              }}>
                {JSON.stringify(data, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── UUID GENERATOR ─────────────────────────────────────────────────── */
export function UUIDGenerator() {
  const [uuids, setUuids] = useState([]);
  const [count, setCount] = useState(5);

  const genV4 = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });

  const generate = () => setUuids(Array.from({ length: count }, genV4));

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ fontSize: 12, color: T.muted, fontFamily: T.sans }}>Count</span>
        <input
          type="number" min={1} max={50} value={count}
          onChange={e => setCount(Math.min(50, Math.max(1, +e.target.value)))}
          style={{ ...S.input, width: 64 }}
        />
        <ActionBtn onClick={generate} color={T.cyan}>↺ Generate</ActionBtn>
      </div>
      {uuids.length > 0 && (
        <div style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {uuids.map((u, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 14px',
              borderBottom: i < uuids.length - 1 ? `1px solid ${T.border}` : 'none',
            }}>
              <code style={{ fontSize: 12.5, fontFamily: T.mono, color: T.white }}>{u}</code>
              <CopyBtn value={u}/>
            </div>
          ))}
        </div>
      )}
      {uuids.length > 1 && (
        <div style={{ marginTop: 8 }}>
          <CopyBtn value={uuids.join('\n')}/>&nbsp;copy all
        </div>
      )}
    </div>
  );
}

/* ─── HASH GENERATOR ─────────────────────────────────────────────────── */
export function HashGenerator() {
  const [input,  setInput]  = useState('');
  const [alg,    setAlg]    = useState('SHA-256');
  const [result, setResult] = useState('');

  useEffect(() => {
    if (!input) { setResult(''); return; }
    (async () => {
      try {
        const buf = await crypto.subtle.digest(alg, new TextEncoder().encode(input));
        setResult(Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join(''));
      } catch {}
    })();
  }, [input, alg]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {['SHA-256', 'SHA-384', 'SHA-512', 'SHA-1'].map(a => (
          <Pill key={a} active={alg === a} onClick={() => setAlg(a)} color={T.purple}>{a}</Pill>
        ))}
      </div>
      <Label>Input Text</Label>
      <textarea
        style={{ ...S.editor, minHeight: 100 }}
        value={input}
        onChange={e => setInput(e.target.value)}
        placeholder="Enter text to hash…"
      />
      {result && (
        <div style={{ marginTop: 12 }}>
          <Label>{alg} Hash — {result.length / 2} bytes</Label>
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: '12px 14px',
            background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 8,
          }}>
            <code style={{ fontSize: 11.5, fontFamily: T.mono, color: T.white, wordBreak: 'break-all', flex: 1 }}>
              {result}
            </code>
            <CopyBtn value={result}/>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── HMAC GENERATOR ─────────────────────────────────────────────────── */
export function HMACGenerator() {
  const [message, setMessage] = useState('');
  const [secret,  setSecret]  = useState('');
  const [alg,     setAlg]     = useState('SHA-256');
  const [result,  setResult]  = useState('');

  useEffect(() => {
    if (!message || !secret) { setResult(''); return; }
    (async () => {
      try {
        const enc = new TextEncoder();
        const key = await crypto.subtle.importKey(
          'raw', enc.encode(secret), { name: 'HMAC', hash: alg }, false, ['sign']
        );
        const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
        setResult(Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join(''));
      } catch {}
    })();
  }, [message, secret, alg]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {['SHA-256', 'SHA-384', 'SHA-512'].map(a => (
          <Pill key={a} active={alg === a} onClick={() => setAlg(a)} color={T.orange}>
            HMAC-{a}
          </Pill>
        ))}
      </div>
      <TwoCol>
        <Col label="Message">
          <textarea style={S.editor} value={message} onChange={e => setMessage(e.target.value)} placeholder="Message to sign…"/>
        </Col>
        <Col label="Secret Key">
          <textarea style={S.editor} value={secret} onChange={e => setSecret(e.target.value)} placeholder="Your secret key…"/>
        </Col>
      </TwoCol>
      {result && (
        <div style={{ marginTop: 12 }}>
          <Label>HMAC Signature</Label>
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: '12px 14px',
            background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 8,
          }}>
            <code style={{ fontSize: 11.5, fontFamily: T.mono, color: T.white, wordBreak: 'break-all', flex: 1 }}>
              {result}
            </code>
            <CopyBtn value={result}/>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── REGEX TESTER ───────────────────────────────────────────────────── */
export function RegexTester() {
  const [pattern, setPattern] = useState('');
  const [flags,   setFlags]   = useState('g');
  const [test,    setTest]    = useState('Hello World 123\nfoo@bar.com\nhttps://example.com');
  const [replace, setReplace] = useState('');
  const [error,   setError]   = useState('');

  let matches = [], highlighted = test, replaced = '', info = null;

  if (pattern && test) {
    try {
      const gFlags = flags.includes('g') ? flags : flags + 'g';
      const rx     = new RegExp(pattern, gFlags);

      const ms     = [...test.matchAll(rx)];
      matches = ms;
      info    = { count: ms.length };
      if (replace !== '') replaced = test.replace(new RegExp(pattern, flags), replace);
      const parts = []; let last = 0;
      ms.forEach(m => {
        parts.push({ text: test.slice(last, m.index), match: false });
        parts.push({ text: m[0], match: true });
        last = m.index + m[0].length;
      });
      parts.push({ text: test.slice(last), match: false });
      highlighted = parts;
      setError('');
    } catch(e) { setError(e.message); }
  }

  return (
    <div>
      {/* Pattern + flags row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Label>Pattern</Label>
          <input
            value={pattern}
            onChange={e => setPattern(e.target.value)}
            style={S.input}
            placeholder="\d+|[a-z]+@\w+"
          />
        </div>
        <div>
          <Label>Flags</Label>
          <div style={{ display: 'flex', gap: 4 }}>
            {['g', 'i', 'm', 's'].map(f => (
              <Pill
                key={f}
                active={flags.includes(f)}
                onClick={() => setFlags(p => p.includes(f) ? p.replace(f, '') : p + f)}
                color={T.green}
              >{f}</Pill>
            ))}
          </div>
        </div>
      </div>

      {/* Replace row */}
      <div style={{ marginBottom: 12 }}>
        <Label>Replace With (optional)</Label>
        <input
          value={replace}
          onChange={e => setReplace(e.target.value)}
          style={S.input}
          placeholder="Replacement ($1 for groups)"
        />
      </div>

      {/* Test + matches */}
      <TwoCol>
        <Col label="Test String">
          <textarea style={S.editor} value={test} onChange={e => setTest(e.target.value)}/>
        </Col>
        <Col label={info ? `Matches (${info.count} found)` : 'Matches'}>
          <div style={{
            padding: '14px 16px',
            background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 8,
            minHeight: 160, fontSize: 13, fontFamily: T.mono, color: T.white,
            lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            boxSizing: 'border-box',
          }}>
            {Array.isArray(highlighted)
              ? highlighted.map((p, i) => (
                  <span key={i} style={{
                    background: p.match ? T.yellow + '35' : 'transparent',
                    color:      p.match ? T.yellow : 'inherit',
                    borderRadius: p.match ? 2 : 0,
                    padding: p.match ? '0 2px' : 0,
                  }}>{p.text}</span>
                ))
              : test}
          </div>
        </Col>
      </TwoCol>

      <ErrorBox msg={error}/>

      {/* Replaced output */}
      {replace !== '' && replaced && (
        <div style={{ marginTop: 12 }}>
          <Label>Replaced Output</Label>
          <textarea style={S.editorReadOnly} value={replaced} readOnly/>
          <div style={{ marginTop: 6 }}><CopyBtn value={replaced}/></div>
        </div>
      )}

      {/* Match details */}
      {matches.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Label>Match Details</Label>
          {matches.slice(0, 10).map((m, i) => (
            <div key={i} style={{
              padding: '6px 12px', marginBottom: 4,
              background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 7,
              fontSize: 12, fontFamily: T.mono,
              display: 'flex', gap: 10, alignItems: 'center',
            }}>
              <span style={{ color: T.dim }}>[{i}]</span>
              <span style={{ color: T.yellow, fontWeight: 600 }}>{m[0]}</span>
              <span style={{ color: T.dim }}>index {m.index}</span>
            </div>
          ))}
          {matches.length > 10 && (
            <div style={{ fontSize: 11, color: T.dim }}>…and {matches.length - 10} more</div>
          )}
        </div>
      )}
    </div>
  );
}